import {
  CheckCircleOutlined,
  DownOutlined,
  FlagOutlined,
  GiftOutlined,
  UpOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Radio, Row, Skeleton, Space, Statistic, Typography } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { DailyParticipantPoint, useAffiliateCampaignDashboard } from "src/hooks/affiliate";

const { Text } = Typography;

const formatNumber = (n?: number) => (n == null ? "-" : n.toLocaleString());

const BAR_COLOR = "#1890ff";
const BAR_HIGHLIGHT = "#0050b3";

interface DailyBarChartProps {
  data: DailyParticipantPoint[];
}

const DailyBarChart: React.FunctionComponent<DailyBarChartProps> = ({ data }) => {
  const width = 600;
  const height = 180;
  const padding = { top: 16, right: 16, bottom: 32, left: 32 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const max = Math.max(1, ...data.map((d) => d.count));
  // Tighter gap when there are many bars
  const barGap = data.length > 14 ? 2 : 8;
  const barWidth = data.length ? (innerW - barGap * (data.length - 1)) / data.length : 0;

  // y-axis ticks: 0, max/2, max (rounded)
  const yTicks = [0, Math.ceil(max / 2), max];

  // For many bars, show date labels every Nth (always include first + last)
  const labelInterval = data.length <= 10
    ? 1
    : data.length <= 16
    ? 2
    : data.length <= 31
    ? 5
    : 7;

  // Show value labels only when bars are wide enough
  const showValueLabels = barWidth >= 14;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="xMidYMid meet">
      {yTicks.map((t, i) => {
        const y = padding.top + innerH - (t / max) * innerH;
        return (
          <g key={i}>
            <line
              x1={padding.left}
              x2={padding.left + innerW}
              y1={y}
              y2={y}
              stroke="#f0f0f0"
              strokeWidth={1}
            />
            <text x={padding.left - 6} y={y + 3} fontSize={10} fill="#8c8c8c" textAnchor="end">
              {t}
            </text>
          </g>
        );
      })}

      {data.map((d, i) => {
        const isToday = i === data.length - 1;
        const h = (d.count / max) * innerH;
        const x = padding.left + i * (barWidth + barGap);
        const y = padding.top + innerH - h;
        const showLabel = i === 0 || i === data.length - 1 || i % labelInterval === 0;
        return (
          <g key={d.date}>
            <rect
              x={x}
              y={y}
              width={Math.max(barWidth, 1)}
              height={Math.max(h, 0)}
              fill={isToday ? BAR_HIGHLIGHT : BAR_COLOR}
              rx={2}
            >
              <title>{`${d.date}: ${d.count} คน`}</title>
            </rect>
            {showValueLabels && d.count > 0 && (
              <text
                x={x + barWidth / 2}
                y={y - 4}
                fontSize={10}
                fill="#595959"
                textAnchor="middle"
              >
                {d.count}
              </text>
            )}
            {showLabel && (
              <text
                x={x + barWidth / 2}
                y={padding.top + innerH + 16}
                fontSize={10}
                fill="#8c8c8c"
                textAnchor="middle"
              >
                {dayjs(d.date).format("DD/MM")}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export const CampaignDashboard: React.FunctionComponent = () => {
  const [days, setDays] = React.useState<7 | 30>(7);
  const [chartExpanded, setChartExpanded] = React.useState(false);
  const { data, isFetching } = useAffiliateCampaignDashboard(days, {
    keepPreviousData: true,
  });

  if (isFetching && !data) {
    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Skeleton active />
      </Card>
    );
  }

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title="Active Campaigns"
            value={formatNumber(data?.activeCampaigns)}
            prefix={<FlagOutlined style={{ color: "#1890ff" }} />}
          />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title="Participants วันนี้"
            value={formatNumber(data?.participantsToday)}
            prefix={<UserOutlined style={{ color: "#52c41a" }} />}
            suffix={<Text type="secondary" style={{ fontSize: 12 }}>คน</Text>}
          />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title="Quest Completed วันนี้"
            value={formatNumber(data?.completedToday)}
            prefix={<CheckCircleOutlined style={{ color: "#13c2c2" }} />}
            suffix={<Text type="secondary" style={{ fontSize: 12 }}>ครั้ง</Text>}
          />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title="Reward Paid วันนี้"
            value={data?.rewardPaidToday ?? 0}
            precision={2}
            prefix={<GiftOutlined style={{ color: "#faad14" }} />}
            suffix={<Text type="secondary" style={{ fontSize: 12 }}>บาท</Text>}
          />
        </Card>
      </Col>
      <Col span={24}>
        <Card
          size="small"
          title={
            <Space>
              <span>คนทำ Quest ต่อวัน</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({data?.days ?? days} วันย้อนหลัง)
              </Text>
            </Space>
          }
          extra={
            <Space size="small">
              {chartExpanded && (
                <Radio.Group
                  size="small"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value={7}>7 วัน</Radio.Button>
                  <Radio.Button value={30}>30 วัน</Radio.Button>
                </Radio.Group>
              )}
              <Button
                type="link"
                size="small"
                onClick={() => setChartExpanded((v) => !v)}
                icon={chartExpanded ? <UpOutlined /> : <DownOutlined />}
              >
                {chartExpanded ? "ย่อ" : "ขยาย"}
              </Button>
            </Space>
          }
        >
          {chartExpanded &&
            (data?.dailyParticipants?.length ? (
              <DailyBarChart data={data.dailyParticipants} />
            ) : (
              <Text type="secondary">ยังไม่มีข้อมูล</Text>
            ))}
        </Card>
      </Col>
    </Row>
  );
};

export default CampaignDashboard;
